import { useContext, useMemo, useState } from 'react';
import EDAvenyProgress from '../../../../../../BaseComponents/ProgressIndicators/AvenyProgress/EDAvenyProgress';
import GravTitelKort from '../GravTitelKort';
import ObjektVySida from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVySida/ObjektVySida';
import ObjektVyKolumner, {
  ObjektVyKolumn,
} from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKolumner/ObjektVyKolumner';
import { ObjektVyKort } from '../../../../_Common/Components/BusinessCollectionComponents/ObjektVyKort/ObjektVyKort';
import { GravplatsQuery_node_TEDGAGrav as IGravplatsNode } from '../Queries/_QueryTypes/GravplatsQuery';
import { useMutation, useQuery } from '@apollo/client';
import GravHandelseAnsvarigAllQuery from '../Queries/GravHandelseAnsvarigQuery.gql';
import GravHandelseRubrikAllQuery from '../Queries/GravHandelseRubrikQuery.gql';
import clientAvenyModel from '../../../../../../Model/clientAvenyModel';
import EDList from '../../../../../../BaseComponents/List/EDList';
import { EDListItemButton } from '../../../../../../BaseComponents/List/Components/EDListItem';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDCaptionedValue from '../../../../../../BaseComponents/CaptionedValue/EDCaptionedValue';
import { makeStyles } from 'tss-react/mui';
import EDCheckbox from '../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDDialog from '../../../../../../BaseComponents/Dialog/EDDialog';
import EDButton from '../../../../../../BaseComponents/Buttons/Button/EDButton';
import { formatAllCapsText } from '../Utils';
import { ActiveDatabaseContext } from '../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { ISuggestion } from '../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import { useIsSmallScreen } from '../../../../../../Hooks/useFullscreenView';
import AddHandelse from '../Queries/GravAddHandelseMutation.gql';
import UpdateHandelse from '../Queries/GravUpdateHandelseMutation.gql';
import {
  TEDGAHandelseKLAREnum,
  TEDGAHandelseTypEnum,
} from '../../../../../../globalQueryTypes';
import { HandelseDialog, IHandelseFormValues } from '../HandelseDialog';

const GravModel = clientAvenyModel.GA.BusinessModels.GAGrav;

interface IHandelserProps {
  sidNr: number;
  nodeData: IGravplatsNode | null | undefined;
  fullPage: boolean;
}

interface IHandelseDetailsProps {
  item: any | null;
}

const useStyles = makeStyles()(theme => ({
  item: {
    '&:hover': {
      background:
        theme.customProperties?.Fade?.BlueGray?.hover ??
        theme.palette.action.hover,
    },
  },
  selectedItem: {
    background:
      theme.customProperties?.Fade?.BlueGray?.selected ??
      theme.palette.action.selected,
  },
  label: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(0.25),
    },
  },
  labelHistorik: {
    color: theme.palette.text.secondary,
  },
  valueTextHistorik: {
    color: theme.palette.text.secondary,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  valuesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    flex: 1,
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(0.5, 1.5),
    },
  },
  valueItem: {
    minWidth: 120,
    [theme.breakpoints.down('sm')]: {
      minWidth: 80,
    },
  },
  valueLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.7rem',
    },
  },
  valueText: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
    },
  },
  secondaryActions: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
  },
  listItemContent: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    padding: theme.spacing(1, 0),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5, 0),
    },
  },
  paper: {
    maxWidth: '670px',
    background: theme.palette.background.default,
  },
}));

const getStatusText = (item: any): string => {
  if (
    item?.KLAR === TEDGAHandelseKLAREnum.NOTERING &&
    item?._Associations?.Parent?._typ === TEDGAHandelseTypEnum.UTSKICK
  ) {
    return 'Utskick';
  }
  return formatAllCapsText(item?.KLAR);
};

const HandelseDetails: React.FC<IHandelseDetailsProps> = ({ item }) => {
  if (!item) return null;

  return (
    <EDBox display="flex" flexDirection="column" gap={2}>
      <EDCaptionedValue label="Rubrik" name="rubrik" size="large">
        <EDTypography>{item?.RUBRIK || '-'}</EDTypography>
      </EDCaptionedValue>
      <EDCaptionedValue label="Text" name="text" size="large">
        <EDTypography sx={{ whiteSpace: 'pre-wrap' }}>
          {item?.TEXT || '-'}
        </EDTypography>
      </EDCaptionedValue>

      <EDCaptionedValue label="Ansvarig" name="ansvarig" size="large">
        <EDTypography>
          {item?._Associations?.Ansvarig?.ANSVARIG_NAMN || '-'}
        </EDTypography>
      </EDCaptionedValue>

      <EDCaptionedValue label="Datum" name="datum" size="large">
        <EDTypography>{item?.DATUM?.split(' ')[0] || '-'}</EDTypography>
      </EDCaptionedValue>

      <EDCaptionedValue
        label="Bevakningsdatum"
        name="bevakningsdatum"
        size="large"
      >
        <EDTypography>{item?.BEVAKNINGSDATUM || '-'}</EDTypography>
      </EDCaptionedValue>

      <EDCaptionedValue label="Signatur" name="signatur" size="large">
        <EDTypography>{item?.SIGNATUR || '-'}</EDTypography>
      </EDCaptionedValue>

      <EDCaptionedValue label="Status" name="status" size="large">
        <EDTypography>{getStatusText(item)}</EDTypography>
      </EDCaptionedValue>
    </EDBox>
  );
};

export const Handelser: React.FC<IHandelserProps> = ({
  sidNr,
  nodeData,
  fullPage,
}) => {
  const { classes, cx } = useStyles();
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const isSmallScreen = useIsSmallScreen();
  const [selectedHandelse, setSelectedHandelse] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHandelse, setEditingHandelse] = useState<any>(null);
  const [isNewHandelse, setIsNewHandelse] = useState(false);
  const [visaHistorik, setVisaHistorik] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [isRefreshingAfterSave, setIsRefreshingAfterSave] = useState(false);

  const handelser = useMemo(
    () => nodeData?._Associations?.Handelser_All ?? [],
    [nodeData]
  );

  const [addHandelse, { loading: addingHandelse }] = useMutation(AddHandelse);
  const [updateHandelse, { loading: updatingHandelse }] =
    useMutation(UpdateHandelse);

  // Ansvarig query
  const { data: ansvarigData, loading: ansvarigLoading } = useQuery(
    GravHandelseAnsvarigAllQuery,
    {
      variables: { db: activeDatabase || '' },
      skip: !activeDatabase,
    }
  );

  // Rubrik query
  const { data: rubrikData, loading: rubrikLoading } = useQuery(
    GravHandelseRubrikAllQuery,
    {
      variables: { db: activeDatabase || '' },
      skip: !activeDatabase,
    }
  );

  const ansvarigOptions = useMemo(() => {
    const items = ansvarigData?.db?.GAGravHandelseAnsvarig_All ?? [];
    return items
      .filter((item: any) => !item.BORTTAGEN)
      .map(
        (item: any) =>
          ({
            value: item.id,
            name: item.ANSVARIG_NAMN,
            data: {
              ID: item.ID,
              ANSVARIG_NAMN: item.ANSVARIG_NAMN,
            },
          } as ISuggestion)
      );
  }, [ansvarigData]);

  const rubrikOptions = useMemo(() => {
    const items = rubrikData?.db?.GAGravHandelseRubrik_All ?? [];
    return items
      .filter((item: any) => !item.BORTTAGEN)
      .map(
        (item: any) =>
          ({
            value: item.id,
            name: item.BENAMNING,
            data: {
              ID: item.ID,
              BENAMNING: item.BENAMNING,
            },
          } as ISuggestion)
      );
  }, [rubrikData]);

  const aktuellaHandelser = useMemo(() => {
    return handelser.filter(item => item?.KLAR?.toUpperCase() !== 'KLAR');
  }, [handelser]);

  const filteredHandelser = useMemo(() => {
    return visaHistorik ? handelser : aktuellaHandelser;
  }, [handelser, aktuellaHandelser, visaHistorik]);

  const isHistorik = (item: any): boolean => {
    return item?.KLAR?.toUpperCase() === 'KLAR';
  };

  const hasHistorik = handelser.length > aktuellaHandelser.length;
  const handelseSaving = addingHandelse || updatingHandelse;

  const effectiveSelectedHandelse =
    selectedHandelse ?? filteredHandelser[0] ?? null;

  const getValues = (item: any) =>
    [
      { label: 'Datum', value: item?.DATUM?.split(' ')[0] },
      { label: 'Signatur', value: item?.SIGNATUR },
      { label: 'Status', value: getStatusText(item) },
      {
        label: 'Ansvarig',
        value: item?._Associations?.Ansvarig?.ANSVARIG_NAMN,
      },
    ].filter(v => v.value && v.value !== '-');

  const handleSelectHandelse = (item: any) => {
    setSelectedHandelse(item);
  };

  const handleOpenNewDialog = () => {
    setIsNewHandelse(true);
    setEditingHandelse(null);
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (item: any) => {
    setIsNewHandelse(false);
    setEditingHandelse(item);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingHandelse(null);
    setIsNewHandelse(false);
  };

  const handleSaveHandelse = async (formValues: IHandelseFormValues) => {
    setIsRefreshingAfterSave(true);
    try {
      if (isNewHandelse) {
        await addHandelse({
          variables: {
            db: activeDatabase || '',
            values: {
              _typ: TEDGAHandelseTypEnum.HANDELSERAD,
              RUBRIK: formValues.rubrik,
              TEXT: formValues.text || '',
              BEVAKNINGSDATUM:
                formValues.bevakning || new Date().toISOString().split('T')[0],
              ANSVARIG_ID: formValues.ansvarigSuggestion?.data?.ID ?? undefined,
              SIGNATUR: formValues.signatur,
              KLAR: formValues.klar ?? TEDGAHandelseKLAREnum.NOTERING,
              _Associations: {
                Grav_Ref: nodeData?.id,
                Parent_Values: {
                  _typ: TEDGAHandelseTypEnum.MANUELL,
                  RUBRIK: formValues.rubrik,
                  SIGNATUR: formValues.signatur,
                  ANSVARIG_ID:
                    formValues.ansvarigSuggestion?.data?.ID ?? undefined,
                },
              },
            },
          },
          refetchQueries: ['GravplatsQuery'],
          awaitRefetchQueries: true,
        });
      } else {
        await updateHandelse({
          variables: {
            db: activeDatabase || '',
            values: {
              id: editingHandelse.id,
              _version: editingHandelse._version,
              RUBRIK: formValues.rubrik,
              TEXT: formValues.text || '',
              BEVAKNINGSDATUM: formValues.bevakning || undefined,
              ANSVARIG_ID: formValues.ansvarigSuggestion?.data?.ID ?? undefined,
              KLAR: formValues.klar ?? undefined,
            },
          },
          refetchQueries: ['GravplatsQuery'],
          awaitRefetchQueries: true,
        });
      }
      handleCloseDialog();
    } finally {
      setIsRefreshingAfterSave(false);
    }
  };

  const isPageLoading =
    ansvarigLoading ||
    rubrikLoading ||
    !nodeData ||
    addingHandelse ||
    updatingHandelse ||
    isRefreshingAfterSave;

  return (
    <ObjektVySida
      sidNr={sidNr}
      caption={'Händelser'}
      collectionName={GravModel.CollectionName}
    >
      <EDBox sx={{ position: 'relative' }}>
        {isPageLoading && <EDAvenyProgress overlay />}
        <ObjektVyKolumner>
          <ObjektVyKolumn fullPage={fullPage}>
            <GravTitelKort nodeData={nodeData} />

            <ObjektVyKort
              className="position-relative"
              backgroundColor={'white'}
              caption="Händelseuppgifter"
            >
              <EDBox
                display={'flex'}
                justifyContent={'space-between'}
                flexDirection={'row'}
                gap={1}
              >
                {hasHistorik && (
                  <EDCheckbox
                    label="Visa historik"
                    checked={visaHistorik}
                    onCheck={setVisaHistorik}
                  />
                )}

                <EDButton
                  label="Ny händelse"
                  color="inherit"
                  onClick={handleOpenNewDialog}
                  outlined
                />
              </EDBox>

              {filteredHandelser.length > 0 ? (
                <EDList noPadding>
                  {filteredHandelser.map((item, index) => {
                    const values = getValues(item);
                    const isSelected =
                      effectiveSelectedHandelse?.id === item?.id;
                    return (
                      <EDListItemButton
                        key={item?.id ?? index}
                        className={cx(classes.item, {
                          [classes.selectedItem]: isSelected,
                        })}
                        divider={index < filteredHandelser.length - 1}
                        disableVerticalPadding
                        selected={isSelected}
                        onClick={() => handleSelectHandelse(item)}
                      >
                        <EDBox className={classes.listItemContent}>
                          <EDBox sx={{ flex: 1 }}>
                            <EDTypography
                              variant="subtitle2"
                              className={cx(classes.label, {
                                [classes.labelHistorik]: isHistorik(item),
                              })}
                            >
                              {item?.RUBRIK || '-'}
                            </EDTypography>

                            <div className={classes.valuesContainer}>
                              {values.map((v, idx) => (
                                <div key={idx} className={classes.valueItem}>
                                  <EDTypography
                                    variant="caption"
                                    className={classes.valueLabel}
                                  >
                                    {v.label}
                                  </EDTypography>
                                  <EDTypography
                                    variant="body2"
                                    className={cx(classes.valueText, {
                                      [classes.valueTextHistorik]:
                                        isHistorik(item),
                                    })}
                                  >
                                    {v.value || '-'}
                                  </EDTypography>
                                </div>
                              ))}
                            </div>
                          </EDBox>

                          <div className={classes.secondaryActions}>
                            <EDIconButton
                              icon="edit"
                              iconSize="medium"
                              margin="none"
                              padding="dense"
                              onClick={e => {
                                e.stopPropagation();
                                handleOpenEditDialog(item);
                              }}
                              tooltip="Redigera"
                            />
                            {isSmallScreen && (
                              <EDIconButton
                                icon="eye_outline"
                                iconSize="medium"
                                margin="none"
                                padding="dense"
                                onClick={e => {
                                  e.stopPropagation();
                                  handleSelectHandelse(item);
                                  setDetailsDialogOpen(true);
                                }}
                                tooltip="Detaljer"
                              />
                            )}
                          </div>
                        </EDBox>
                      </EDListItemButton>
                    );
                  })}
                </EDList>
              ) : (
                <EDTypography>Inga händelser registrerade</EDTypography>
              )}
            </ObjektVyKort>
          </ObjektVyKolumn>
          {!isSmallScreen && (
            <ObjektVyKolumn fullPage={fullPage}>
              <EDBox
                sx={{
                  position: 'sticky',
                  top: 0,
                }}
              >
                {handelser.length > 0 && effectiveSelectedHandelse ? (
                  <ObjektVyKort
                    className="position-relative"
                    backgroundColor={'white'}
                    caption="Detaljer"
                  >
                    <HandelseDetails item={effectiveSelectedHandelse} />
                  </ObjektVyKort>
                ) : (
                  <ObjektVyKort
                    className="position-relative"
                    backgroundColor={'white'}
                    caption="Detaljer"
                  >
                    <EDTypography>Inga händelser registrerade</EDTypography>
                  </ObjektVyKort>
                )}
              </EDBox>
            </ObjektVyKolumn>
          )}
        </ObjektVyKolumner>
      </EDBox>

      <HandelseDialog
        key={
          dialogOpen ? (isNewHandelse ? 'new' : editingHandelse?.id) : 'closed'
        }
        open={dialogOpen}
        isNew={isNewHandelse}
        editingItem={editingHandelse}
        rubrikOptions={rubrikOptions}
        ansvarigOptions={ansvarigOptions}
        paperClassName={classes.paper}
        loading={handelseSaving}
        onClose={handleCloseDialog}
        onSave={handleSaveHandelse}
      />

      {/* Dialog för detaljer på mobilskärm */}
      <EDDialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        title="Detaljer"
        subtitle={effectiveSelectedHandelse?.RUBRIK}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.paper }}
        footerActions={[
          { label: 'Stäng', onClick: () => setDetailsDialogOpen(false) },
        ]}
      >
        <HandelseDetails item={effectiveSelectedHandelse} />
      </EDDialog>
    </ObjektVySida>
  );
};

export default Handelser;
